import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { AnalyticAppName } from '@core/constants/constants';
import { HttpStatusCodes } from '@core/enums/http-status-codes.enum';
import { Message } from '@core/models/message.model';
import { AppSettingsService } from '@core/services/app-settings/app-settings.service';
import { MonitorService } from '@core/services/monitor/monitor.service';
import { ConnectionService } from '@core/services/network-connection/network-connection.service';
import { ServerErrorConnectorService } from '@core/services/server-error-connector/server-error-connector.service';
import { UserService } from '@core/services/user/user.service';
import { User } from '@core/states/user/user-state.model';
import { BannerService } from '@shared/components/banner/banner.service';
import { LoadingCircularPanelService } from '@shared/components/loading-circular-panel/loading-circular-panel.service';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable()
export class ServerErrorInterceptor implements HttpInterceptor {
  constructor(
    private injector: Injector,
    private serverErrorConnectorService: ServerErrorConnectorService,
    private connectionService: ConnectionService,
    private userService: UserService,
    private monitorService: MonitorService
  ) { }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        this.monitorEvent(error);
        this.closeWaitingPanel();
        this.handleHttpErrorResponse(error);
        return throwError(error);
      })
    );
  }

  private monitorEvent(error: HttpErrorResponse): void {
    const appSettingsService = this.injector.get(AppSettingsService);
    const monitorService = this.injector.get(MonitorService);

    const monitorError = new Error(
      appSettingsService.configuration.monitorSettings.serverErrorMessageName
    );
    monitorError.name = 'Error catched by ServerErrorInterceptor';
    monitorError.message =
      !!error && !!error.message ? error.message : 'cannot log error message';
    monitorError.stack =
      !!error && !!error.statusText
        ? error.statusText
        : 'cannot log error statusText';

    monitorService.logError(
      {
        error: monitorError,
        properties: {
          name: AnalyticAppName,
          message: 'ServerErrorInterceptor'
        }
      },
      {
        sendToAppInsight: true,
        sendToHeap: true
      }
    );
  }

  private closeWaitingPanel(): void {
    const loadingPanelService = this.injector.get(LoadingCircularPanelService);
    loadingPanelService.toggleLoadingCircularPanelVisibility(false);
  }

  private handleHttpErrorResponse(error: HttpErrorResponse): void {
    const appSettingsService = this.injector.get(AppSettingsService);
    const bannerService = this.injector.get(BannerService);

    if (!!error && this.connectionService.isOnline) {
      switch (error.status) {
        case HttpStatusCodes.Unauthorized:
          {
            this.serverErrorConnectorService.setSessionExpiration(true);
          }
          break;
        case HttpStatusCodes.Forbidden:
          {
            if (!this.isInWhiteList(error.url)) {
              bannerService.showMessage(
                Message.create(
                  appSettingsService.configuration.errorSettings
                    .accessDeniedErrorMessage,
                  'critical'
                )
              );
            }
          }
          break;

        case HttpStatusCodes.NotFound:
          {
            if (error.url.toLowerCase().includes('trainingrecord/documents')) {
              bannerService.showMessage(
                Message.create(error.error, 'critical')
              );
            }
          }
          break;
        default: {
          if (!this.isInWhiteList(error.url)) {
            bannerService.showMessage(
              Message.create(
                appSettingsService.configuration.errorSettings
                  .defaultErrorMessage,
                'critical'
              )
            );
          }
        }
      }

      this.userService.getUser().subscribe((user: User) => {
        if (user) {
          this.monitorService.logError(
            {
              error: {
                ...error,
                name: 'CCT-Web-BackEndError'
              },
              properties: user
            },
            {
              sendToAppInsight: true,
              sendToHeap: true
            }
          );
        }
      });
    }
  }

  private isInWhiteList(url: string): boolean {
    let isInWhiteList = false;
    const whiteList = [
      'reservationdocuments/tsa',
      'api/v1/Schedule',
      'RecentlyViewedDocuments',
      'DocumentViewerStatus'
    ];
    whiteList.forEach((s) => {
      if (url.toLowerCase().includes(s.toLowerCase())) {
        isInWhiteList = true;
      }
    });
    return isInWhiteList;
  }
}
